import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { Scadenza } from 'src/app/common/models/scadenza';
import { MotivoScadenzaAuto } from 'src/app/common/models/tipologiche/motivo-scadenza-auto';
import { Priorita } from 'src/app/common/models/tipologiche/priorita';
import { TipoScadenza } from 'src/app/common/models/tipologiche/tipo-scadenza';
import { CreazionePraticaStoreService } from '../../../../services/creazione-pratica-store.service';
import { PropertiesSettings } from 'src/app/core/configuration/properties-settings';
import { CreazionePraticaTipologicheService } from '../../../../services/creazione-pratica-tipologiche.service';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { Utente } from 'src/app/common/models/utente';
import { Motivo } from 'src/app/common/models/tipologiche/motivo';
import { ModalNotifyService } from 'src/app/core/services/modal-notify.service';
import { ViewManagerService } from 'src/app/core/services/view-manager-service';

@Component({
  selector: 'step-adempimenti-form',
  templateUrl: './step-adempimenti-form.component.html',
  styleUrls: ['./step-adempimenti-form.component.css']
})
export class StepAdempimentiFormComponent implements OnInit, OnDestroy {

  PropertiesSettings: PropertiesSettings;
  public scadenzaForm: FormGroup;
  @Input() scadenza: Scadenza;
  public ora: string;
  public oraSet: string;
  public minSet: string;
  isAttivitaTipoScadenza: boolean = false;
  requestSub: Subscription;
  public tipoScadenzaOptions: TipoScadenza[] = [];
  public motivoScadenzaOptions: MotivoScadenzaAuto[] = [];
  public prioritaOptions: Priorita[] = [];
  motivoEvasioneOptions: Motivo[] = [];
  motivoScadenzaOptionsFiltrate: MotivoScadenzaAuto[] = [];
  listaAssegnatari: Utente [] = [];
  filteredAssegnatari: any[] = [];
  // Subject che emetterà i termini di ricerca
  searchInput$ = new Subject<string>();
  // Observable che fornirà gli assegnatari filtrati
  filteredAssegnatari$: Observable<any[]>;

  private componentDestroyed$: Subject<void> = new Subject<void>();

  @Output() adempimentoChange: EventEmitter<Scadenza> = new EventEmitter<Scadenza>();

  constructor(
    private helpers: HelpersService, 
    private campiForm: FormBuilder, 
    private tipologicheService: CreazionePraticaTipologicheService, 
    private creazionePraticaStore: CreazionePraticaStoreService,
    private modalNotifyService: ModalNotifyService,
    private viewManager: ViewManagerService, 
  ) {
  }
  
  ngOnInit(): void {
    this.populateSelect();
    this.createForm();
    if (this.scadenza) {
      const data = new Date(this.scadenza.dataScadenza);
      this.oraSet = data.getHours().toString().padStart(2, '0');
      this.minSet = data.getMinutes().toString().padStart(2, '0');
      this.ora = this.oraSet + ":" + this.minSet;
    }
  }

  ngOnDestroy(){
    this.componentDestroyed$.next();
  }

  createForm() {
    this.oraSet = this.oraSet || "00";
    this.minSet = this.minSet || "00";
    this.ora = this.oraSet+":"+this.minSet;
    this.isAttivitaTipoScadenza = false;
    this.scadenzaForm = this.campiForm.group({
      tipoScadenza: [this.scadenza?.tipoScadenza, Validators.required],
      motivo: [this.scadenza?.motivo, Validators.required],
      priorita: [this.scadenza?.priorita, Validators.required],
      dataScadenza: [this.scadenza?.dataScadenza, Validators.required],
      dataPromemoria: [this.scadenza?.dataPromemoria],
      pubblica: [this.scadenza?.pubblica || false],
      evasa: [this.scadenza?.evasa || false],
      dataEvasione: [{value: this.scadenza?.dataEvasione, disabled: !this.scadenza?.evasa}],
      motivoEvasione: [this.scadenza?.motivoEvasione],
      appunto: [this.scadenza?.appunto || ''],
      note: [this.scadenza?.note],
      assegnatario: [this.scadenza?.assegnatario],
      dettaglioEsitoAdempimento: [this.scadenza?.dettaglioEsitoAdempimento]
    });
    this.scadenzaForm.get('tipoScadenza').valueChanges.pipe(distinctUntilChanged()).subscribe(value => this.onChangeTipoScadenza(value));
    this.scadenzaForm.get('evasa').valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
      if (value) {
          this.scadenzaForm.get('dataEvasione').setValue(null);
          this.scadenzaForm.get('dataEvasione').enable();
          this.scadenzaForm.get('dataEvasione').setValidators([Validators.required])
      } else {
          this.scadenzaForm.get('dataEvasione').clearValidators();
          this.scadenzaForm.get('dataEvasione').reset();
          this.scadenzaForm.get('dataEvasione').disable();
      }
    });
    if (this.scadenza && this.scadenza.tipoScadenza && this.scadenza.tipoScadenza.id === 'ATTIVITA') {
      this.isAttivitaTipoScadenza = true;
    }
    const assegnatarioSalvato = this.listaAssegnatari?.find(a => a.username === this.scadenza.assegnatario);
    if (assegnatarioSalvato) {
      this.scadenzaForm.controls['assegnatario'].setValue(assegnatarioSalvato);
    }
    this.termSearch();
  }

  private termSearch() {
    this.filteredAssegnatari$ = this.searchInput$.pipe(
      switchMap(term => {
        if (term && term.length >= 2) {
          return this.filterAssegnatari(term);
        } else {
          return of([]);
        }
      })
    );
  }
  //Occorre confrontare il valore selezionato con gli oggetti nel dropdown essendo esso una string(bindValue) mentre la lista un oggetto
  compareFn(a, b) {
    return a == b;
  }

  // Funzione che filtra la lista in base al termine di ricerca
    filterAssegnatari(term: string): Observable<any[]> {
    if (!term) {
      // Se non c'è nessun termine, ritorna un array vuoto (così la lista non viene mostrata)
      return of([]);
    }
    const lowerTerm = term.toLowerCase();
    const filtered = this.listaAssegnatari.filter(item =>
      item.nome.toLowerCase().includes(lowerTerm) || 
      item.cognome.toLowerCase().includes(lowerTerm)
    );
    return of(filtered);
  }
  

  submit() {
    if(this.scadenzaForm.valid){
      this.scadenza = this.scadenzaForm.getRawValue();
      let date = new Date(this.scadenza.dataScadenza);
      let oraMinuti: string[] = this.ora.split(':');
      date.setHours(Number(oraMinuti[0]), Number(oraMinuti[1]));
      this.scadenza.dataScadenza = date.getTime();
      console.log(this.scadenza, 'scad')
      this.adempimentoChange.emit(this.scadenza);
      this.viewManager._prossimiAdempimenti.next({param: null, timeOut:true});
    }
  }

  editScadenza() {
    if (!this.scadenzaForm.valid) return;
    let idScadenza = this.scadenza.id;
    const documenti = this.scadenza.documenti;
    this.scadenza = this.scadenzaForm.getRawValue();
    this.scadenza.documenti = documenti;
    this.scadenza.assegnatario = this.scadenzaForm.getRawValue().assegnatario || null;
    this.scadenza.dettaglioEsitoAdempimento = this.scadenzaForm.getRawValue().dettaglioEsitoAdempimento || null;
    let date = new Date(this.scadenza.dataScadenza);
    let oraMinuti: string[] = this.ora.split(':');
    date.setHours(Number(oraMinuti[0]), Number(oraMinuti[1]));
    this.scadenza.dataScadenza = date.getTime();
    this.scadenza.id = idScadenza;  
    // Controllo se è ATTIVITA e se assegnatario è valorizzato prima di mostrare la notifica
    if (this.scadenza.tipoScadenza.id === 'ATTIVITA' && this.scadenza.assegnatario) {
      this.requestSub = this.modalNotifyService.notifyConfirmObservable.subscribe(result => {
        this.scadenza.inviaNotifica = result;
        this.adempimentoChange.emit(this.scadenza);
        this.viewManager._prossimiAdempimenti.next({param: null, timeOut:true});
      });  
      this.modalNotifyService.notifyCustom("Sono state apportate delle modifiche. Confermi di voler rinviare la notifica all'utente assegnatario?");
    } else {
      console.log(this.scadenza, 'scad');
      this.adempimentoChange.emit(this.scadenza);
    }
  }

  onChangeTipoScadenza(event: TipoScadenza){
      this.scadenzaForm.get('tipoScadenza').setValue(event);
      this.scadenzaForm.get('motivo').setValue(null);
      if(event){
          this.motivoScadenzaOptionsFiltrate = this.motivoScadenzaOptions.filter(scadenza => scadenza.tipoScadenza.id == event.id);
      }
      else{
          this.motivoScadenzaOptionsFiltrate = this.motivoScadenzaOptions;
      }
      if(event && event.id === 'ATTIVITA'){
        this.isAttivitaTipoScadenza = true
      } else {
        this.isAttivitaTipoScadenza = false;
        this.scadenzaForm.get('assegnatario').setValue(null);
      }
      if(event.id != 'UDIENZA' && this.scadenza.dettaglioEsitoAdempimento){
        this.scadenza.dettaglioEsitoAdempimento = null; 
      }
  }

  onAssegnatarioSelected(event: any): void {
    if (this.scadenza) {
      this.scadenza.assegnatario = event ? event.username : null;
    }
  }
  

  populateSelect(){
    if(this.motivoEvasioneOptions.length === 0) {
      this.helpers.getMotiviEvadiScadenza().subscribe(response => {
        this.motivoEvasioneOptions = response;
      });
    }
    this.tipologicheService.getTipiScadenza().pipe(takeUntil(this.componentDestroyed$)).subscribe(response => {
      this.tipoScadenzaOptions = response;
      /*
      LOGICA PER MOSTRARE IL TIPO D'ADEMPIMENTO INCONTRO CGS, REINSTATARE AD IMPLEMENTAZIONE COMPLETA DEL TIPO
      if(this.creazionePraticaStore.getTipoFascicolo() != null && this.creazionePraticaStore.getTipoFascicolo().label != "SINISTRO SANITARIO")
        this.tipoScadenzaOptions = response.filter(el => el.id != 'INCONTRO');
      else
        this.tipoScadenzaOptions = response;*/
      // this.tipoScadenzaOptions = response.filter(el => el.id != 'INCONTRO');
    });
    this.tipologicheService.getPrioritaScadenza().pipe(takeUntil(this.componentDestroyed$)).subscribe(response => this.prioritaOptions = response);
    this.tipologicheService.getMotivoScadenza().pipe(takeUntil(this.componentDestroyed$)).subscribe(response => {
      this.motivoScadenzaOptions = response;
      this.motivoScadenzaOptionsFiltrate = response;
    });
    this.creazionePraticaStore.fascicolo$.pipe(take(1)).subscribe(fascicolo => {
      if (fascicolo && fascicolo.id) {
        const idFascicolo = fascicolo.id;
        this.helpers.getAssegnatariAdempimenti(idFascicolo).subscribe(response => {
          this.listaAssegnatari = response
            .map(user => {
              const nome = user.nome ? user.nome : '';
              const cognome = user.cognome ? user.cognome : '';
  
              return {
                ...user,
                cognomeNome: `${cognome} ${nome}`, // Concatenazione dei valori di nome e cognome
                username: user.username,
              };
            })
            .sort((a, b) => a.cognomeNome.localeCompare(b.cognomeNome)); // Ordinamento per cognomeNome
            const assegnatarioSalvato = this.listaAssegnatari.find(a => a.username === this.scadenza?.assegnatario);
            if (assegnatarioSalvato) {
              this.scadenzaForm.controls['assegnatario'].setValue(assegnatarioSalvato);
            }
          });
        }      
    });
  }

  //passo il valore dell'elemento selezionato nel dropdown
  onSearchChange(searchTerm: string): void {
    // Invia il termine di ricerca a searchInput$
    this.searchInput$.next(searchTerm);
    this.termSearch();
  }
  onEvasa(event) {
    this.scadenzaForm.get('evasa').setValue(event);
    this.scadenzaForm.get('motivoEvasione').setValue(null);
    this.scadenzaForm.get('dataEvasione').setValue(null);
    if(event) {
      this.scadenzaForm.get('dataEvasione').setValidators([Validators.required]);
    } else {
      this.scadenzaForm.get('dataEvasione').setValidators([]);
    }
    // this.scadenza.evasa = event; 
    // this.scadenza.dataEvasione = null
    // this.scadenza.motivoEvasione = null;
  }

}
